import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type TouchEvent,
} from "react";
import { cardImageUrl } from "../assets/cards";
import { Phase } from "../gen/game/v1/envelope_pb";
import { useGameStore } from "../store/gameStore";
import styles from "./App.module.css";

type GameScreen = "loading" | "join" | "lobby" | "avatars";

const cookieConsentStorageKey = "p1.cookieConsentAccepted";
const roomCodeSessionKey = "p1.roomCode";
const playerNameSessionKey = "p1.playerName";
const publicGameUrl =
  import.meta.env.VITE_PUBLIC_GAME_URL || window.location.origin;

function roomCodeFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const value = params.get("room") ?? params.get("code") ?? "";
  return value.trim().toUpperCase();
}

function removeRoomCodeFromUrl() {
  const url = new URL(window.location.href);
  const hadRoomCode = url.searchParams.has("room");
  const hadCode = url.searchParams.has("code");
  if (!hadRoomCode && !hadCode) {
    return;
  }

  url.searchParams.delete("room");
  url.searchParams.delete("code");
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, "", nextUrl);
}

function clearStoredRoomCode() {
  sessionStorage.removeItem(roomCodeSessionKey);
  localStorage.removeItem(roomCodeSessionKey);
}

function storedPlayerName() {
  return (
    sessionStorage.getItem(playerNameSessionKey) ??
    localStorage.getItem(playerNameSessionKey) ??
    ""
  );
}

function debugLog(event: string, data: Record<string, unknown> = {}) {
  if (!new URLSearchParams(window.location.search).has("debug")) {
    return;
  }

  console.info(`[p1-client] ${event}`, data);
}

const avatars = [
  avatar("base:carrot", "Морковь"),
  avatar("base:red_pepper", "Красный перец"),
  avatar("base:beet", "Свекла"),
  avatar("base:lemon", "Лимон"),
  avatar("base:blue_mushroom", "Синий гриб"),
  avatar("base:eggplant", "Баклажан"),
  avatar("base:green_apple", "Зеленое яблоко"),
  avatar("base:mango", "Манго"),
  avatar("base:peach", "Персик"),
  avatar("base:pear", "Груша"),
  avatar("base:pink_tomato", "Розовый томат"),
  avatar("base:red_mushroom", "Красный гриб"),
  avatar("base:red_tomato", "Томат"),
  avatar("base:yellow_pepper", "Желтый перец"),
];

export function GameClient() {
  const store = useGameStore();
  const urlRoomCodeRef = useRef(roomCodeFromUrl());
  const initialNameRef = useRef(storedPlayerName());
  const autoJoinAttemptedRef = useRef(false);
  const urlAutoJoinPendingRef = useRef(false);
  const [view, setView] = useState<GameScreen>("loading");
  const [roomInput, setRoomInput] = useState(
    () =>
      urlRoomCodeRef.current ||
      (sessionStorage.getItem(roomCodeSessionKey) ??
        localStorage.getItem(roomCodeSessionKey) ??
        ""),
  );
  const [nameInput, setNameInput] = useState(() => initialNameRef.current);
  const [selectedAvatarId, setSelectedAvatarId] = useState(avatars[0].id);
  const [cookieAccepted, setCookieAccepted] = useState(
    () => localStorage.getItem(cookieConsentStorageKey) === "true",
  );
  const [pendingJoin, setPendingJoin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [matchMenuOpen, setMatchMenuOpen] = useState(false);
  const [matchExitConfirmOpen, setMatchExitConfirmOpen] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const loadedSentKey = useRef("");
  const viewRef = useRef<GameScreen>("loading");
  const isMatchRef = useRef(false);
  const enteredRoomRef = useRef(false);
  const exitToJoinRef = useRef<() => void>(() => {});

  const consumeUrlRoomCode = useCallback((reason: string) => {
    if (!urlRoomCodeRef.current) {
      return;
    }

    debugLog("consume url room code", {
      reason,
      urlRoomCode: urlRoomCodeRef.current,
      currentRoomCode: useGameStore.getState().roomCode,
    });
    urlRoomCodeRef.current = "";
    urlAutoJoinPendingRef.current = false;
    removeRoomCodeFromUrl();
  }, []);

  const phase = store.snapshot?.phase ?? Phase.LOBBY;
  const players = store.snapshot?.players ?? [];
  const currentPlayer = players.find(
    (player) => player.playerId === store.playerId,
  );
  const currentAvatarId = currentPlayer?.avatarId || selectedAvatarId;
  const selectedAvatar = avatarById(currentAvatarId);
  const takenAvatarIds = new Set(
    players
      .filter((player) => player.playerId !== store.playerId)
      .map((player) => player.avatarId),
  );
  const displayName = store.playerName || nameInput || "%ИМЯ%";
  const isCurrentPlayerReady = currentPlayer?.ready ?? false;
  const lobbyDeadlineMs = store.snapshot?.hasDeadline
    ? Number(store.snapshot.deadlineUnixMs)
    : 0;
  const lobbyStartRemainingSec =
    phase === Phase.LOBBY && lobbyDeadlineMs > 0
      ? Math.max(0, Math.ceil((lobbyDeadlineMs - nowMs) / 1000))
      : 0;
  const isLobbyStartLocked =
    phase === Phase.LOBBY && lobbyDeadlineMs > 0 && lobbyStartRemainingSec <= 0;
  const lobbyStartOverlayLabel = isLobbyStartLocked
    ? "GO"
    : lobbyStartRemainingSec > 0
      ? String(lobbyStartRemainingSec)
      : "";
  const isMatch = phase !== Phase.LOBBY;
  const isJoining =
    pendingJoin ||
    store.joinStatus === "pending" ||
    urlAutoJoinPendingRef.current;
  const pauseOverlay =
    store.isPaused && store.roomCode ? <PauseOverlay /> : null;

  viewRef.current = view;
  isMatchRef.current = isMatch;

  useEffect(() => {
    debugLog("mount", {
      urlRoomCode: urlRoomCodeRef.current,
      hasInitialName: initialNameRef.current.trim() !== "",
      storedRoomCode:
        sessionStorage.getItem(roomCodeSessionKey) ??
        localStorage.getItem(roomCodeSessionKey) ??
        "",
      storedSessionRoomCode:
        sessionStorage.getItem("p1.session.roomCode") ??
        localStorage.getItem("p1.session.roomCode") ??
        "",
    });

    const timer = window.setTimeout(() => {
      const state = useGameStore.getState();
      if (urlRoomCodeRef.current) {
        debugLog("initial timer url-room branch", {
          roomCode: state.roomCode,
          playerId: state.playerId,
          hasSessionToken: state.sessionToken !== "",
          joinStatus: state.joinStatus,
          status: state.status,
          currentView: viewRef.current,
        });

        if (
          state.roomCode === urlRoomCodeRef.current &&
          state.playerId &&
          state.sessionToken
        ) {
          debugLog("initial timer url-room resume", {
            roomCode: state.roomCode,
            playerId: state.playerId,
            status: state.status,
          });
          state.resumeConnection();
          return;
        }

        if (state.joinStatus !== "succeeded") {
          setView("join");
        }
        return;
      }

      if (state.roomCode && state.playerId && state.sessionToken) {
        debugLog("initial timer resume", {
          roomCode: state.roomCode,
          playerId: state.playerId,
          status: state.status,
        });
        state.resumeConnection();
        return;
      }

      debugLog("initial timer show join");
      setView("join");
    }, 850);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    debugLog("state", {
      view,
      socketStatus: store.status,
      joinStatus: store.joinStatus,
      roomCode: store.roomCode,
      playerId: store.playerId,
      phase,
      players: players.length,
      hasSnapshot: store.snapshot !== null,
      pendingJoin,
      urlAutoJoinPending: urlAutoJoinPendingRef.current,
    });
  }, [
    phase,
    pendingJoin,
    players.length,
    store.joinStatus,
    store.playerId,
    store.roomCode,
    store.snapshot,
    store.status,
    view,
  ]);

  useEffect(() => {
    const urlRoomCode = urlRoomCodeRef.current;
    const playerName = nameInput.trim();
    const hasResumeSession =
      store.roomCode === urlRoomCode && store.playerId && store.sessionToken;

    if (
      autoJoinAttemptedRef.current ||
      !urlRoomCode ||
      !playerName ||
      !initialNameRef.current.trim() ||
      hasResumeSession
    ) {
      return;
    }

    autoJoinAttemptedRef.current = true;
    urlAutoJoinPendingRef.current = true;
    debugLog("url autojoin connect", {
      urlRoomCode,
      hasPlayerName: playerName !== "",
      status: store.status,
      existingRoomCode: store.roomCode,
      existingPlayerId: store.playerId,
    });
    setView("join");
    store.disconnect();
    store.resetSession();
    store.setMode("player");
    store.setPlayerName(playerName);
    store.connect(store.url, "player", playerName);
  }, [nameInput, store]);

  useEffect(() => {
    if (!urlAutoJoinPendingRef.current || store.status !== "connected") {
      return;
    }

    const urlRoomCode = urlRoomCodeRef.current;
    const playerName = nameInput.trim();
    if (!urlRoomCode || !playerName) {
      debugLog("url autojoin cancelled before join", {
        urlRoomCode,
        hasPlayerName: playerName !== "",
      });
      urlAutoJoinPendingRef.current = false;
      return;
    }

    debugLog("url autojoin send join", {
      urlRoomCode,
      playerName,
      status: store.status,
    });
    store.joinRoom(urlRoomCode, playerName);
    urlAutoJoinPendingRef.current = false;
  }, [nameInput, store]);

  useEffect(() => {
    const normalizedRoomInput = roomInput.trim().toUpperCase();
    if (!normalizedRoomInput) {
      clearStoredRoomCode();
      return;
    }

    sessionStorage.setItem(roomCodeSessionKey, normalizedRoomInput);
    localStorage.setItem(roomCodeSessionKey, normalizedRoomInput);
  }, [roomInput]);

  useEffect(() => {
    sessionStorage.setItem(playerNameSessionKey, nameInput);
    localStorage.setItem(playerNameSessionKey, nameInput);
    if (nameInput && store.playerName !== nameInput) {
      store.setPlayerName(nameInput);
    }
  }, [nameInput, store]);

  useEffect(() => {
    if (!displayName || displayName === "%ИМЯ%" || !store.roomCode) {
      document.title = "Овощной переполох";
      return;
    }

    document.title = `ОП: ${displayName}-${store.roomCode}`;
  }, [displayName, store.roomCode]);

  useEffect(() => {
    if (urlAutoJoinPendingRef.current) {
      debugLog("manual pending join skipped by url autojoin", {
        roomInput,
        status: store.status,
      });
      return;
    }

    if (!pendingJoin || store.status !== "connected") {
      return;
    }

    store.joinRoom(roomInput, nameInput);
    debugLog("pending join send join", {
      roomInput,
      hasName: nameInput.trim() !== "",
      status: store.status,
    });
    setPendingJoin(false);
  }, [nameInput, pendingJoin, roomInput, store]);

  useEffect(() => {
    if (
      view === "loading" &&
      !store.roomCode &&
      (store.status === "closed" || store.status === "error")
    ) {
      debugLog("loading closed/error show join", {
        status: store.status,
        error: store.error,
      });
      setView("join");
    }
  }, [store.error, store.roomCode, store.status, view]);

  useEffect(() => {
    if (
      store.joinStatus !== "succeeded" ||
      !store.roomCode ||
      !store.playerId
    ) {
      if (enteredRoomRef.current && !store.roomCode) {
        debugLog("entered room lost room code", {
          joinStatus: store.joinStatus,
          playerId: store.playerId,
          status: store.status,
          error: store.error,
        });
        loadedSentKey.current = "";
        setPendingJoin(false);
        setMenuOpen(false);
        setMatchMenuOpen(false);
        setMatchExitConfirmOpen(false);
        setView("join");
      }
      enteredRoomRef.current = false;
      return;
    }

    if (!enteredRoomRef.current) {
      enteredRoomRef.current = true;
      debugLog("entered lobby", {
        roomCode: store.roomCode,
        playerId: store.playerId,
        joinStatus: store.joinStatus,
        status: store.status,
      });
      if (urlRoomCodeRef.current === store.roomCode) {
        consumeUrlRoomCode("entered-room");
      }
      setRoomInput(store.roomCode);
      setView("lobby");
    }
  }, [
    store.error,
    store.joinStatus,
    store.playerId,
    store.roomCode,
    store.status,
    consumeUrlRoomCode,
  ]);

  useEffect(() => {
    if (phase === Phase.LOBBY) {
      loadedSentKey.current = "";
      return;
    }

    if (phase !== Phase.LOADING) {
      return;
    }

    const loadedKey = `${store.roomCode}:${store.playerId}:${phase}`;
    if (loadedSentKey.current === loadedKey) {
      return;
    }

    loadedSentKey.current = loadedKey;
    store.loaded();
  }, [phase, store]);

  useEffect(() => {
    if (isMatch) {
      setMenuOpen(false);
      return;
    }

    if (!isMatch) {
      setMatchMenuOpen(false);
      setMatchExitConfirmOpen(false);
    }
  }, [isMatch]);

  useEffect(() => {
    if (!lobbyDeadlineMs) {
      return;
    }

    setNowMs(Date.now());
    const interval = window.setInterval(() => setNowMs(Date.now()), 250);
    return () => window.clearInterval(interval);
  }, [lobbyDeadlineMs]);

  useEffect(() => {
    window.history.replaceState(
      { ...window.history.state, p1Guard: "root" },
      "",
    );
    removeRoomCodeFromUrl();
    window.history.pushState({ p1Guard: "game" }, "");

    const onPopState = () => {
      const currentState = useGameStore.getState();
      const currentPhase = currentState.snapshot?.phase ?? Phase.LOBBY;
      const hasRoom = currentState.roomCode !== "";
      debugLog("popstate", {
        currentPhase,
        hasRoom,
        view: viewRef.current,
        status: currentState.status,
        joinStatus: currentState.joinStatus,
        roomCode: currentState.roomCode,
        playerId: currentState.playerId,
      });

      if (currentPhase !== Phase.LOBBY) {
        window.history.pushState({ p1Guard: "game" }, "");
        window.alert(
          "Выход во время матча недоступен. Игра уже идет, данные хода могут быть потеряны.",
        );
        return;
      }

      if (hasRoom && viewRef.current !== "join") {
        debugLog("popstate exit to join");
        exitToJoinRef.current();
        return;
      }

      debugLog("popstate show join");
      setView("join");
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isMatchRef.current) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  useEffect(() => {
    const preventBrowserZoom = (event: Event) => event.preventDefault();
    const listenerOptions = { passive: false };

    document.addEventListener(
      "gesturestart",
      preventBrowserZoom,
      listenerOptions,
    );
    document.addEventListener(
      "gesturechange",
      preventBrowserZoom,
      listenerOptions,
    );

    return () => {
      document.removeEventListener("gesturestart", preventBrowserZoom);
      document.removeEventListener("gesturechange", preventBrowserZoom);
    };
  }, []);

  const submitJoin = () => {
    const roomCode = roomInput.trim().toUpperCase();
    const playerName = nameInput.trim();

    if (!roomCode || !playerName) {
      return;
    }

    store.setMode("player");
    store.setPlayerName(playerName);
    setRoomInput(roomCode);
    setPendingJoin(true);
    debugLog("manual submit join", {
      roomCode,
      hasName: playerName !== "",
      status: store.status,
    });

    if (store.status === "connected") {
      store.joinRoom(roomCode, playerName);
      setPendingJoin(false);
      return;
    }

    store.connect(store.url, "player", playerName);
  };

  const exitToJoin = () => {
    debugLog("exit to join", {
      view: viewRef.current,
      roomCode: store.roomCode,
      playerId: store.playerId,
      status: store.status,
      joinStatus: store.joinStatus,
      phase,
    });
    store.leaveRoom();
    store.disconnect();
    store.resetSession();
    consumeUrlRoomCode("exit-to-join");
    clearStoredRoomCode();
    loadedSentKey.current = "";
    enteredRoomRef.current = false;
    setRoomInput("");
    setPendingJoin(false);
    setMenuOpen(false);
    setMatchMenuOpen(false);
    setMatchExitConfirmOpen(false);
    setView("join");
    window.history.pushState({ p1Guard: "game" }, "");
  };
  exitToJoinRef.current = exitToJoin;

  const toggleMenu = () => setMenuOpen((open) => !open);

  if (isMatch) {
    return (
      <main className={styles.mobileShell}>
        <TopBar onMenu={() => setMatchMenuOpen(true)} />
        <MatchStage onExitToJoin={exitToJoin} />
        {matchMenuOpen && (
          <MatchMenuOverlay
            onClose={() => setMatchMenuOpen(false)}
            onExit={() => {
              setMatchMenuOpen(false);
              setMatchExitConfirmOpen(true);
            }}
          />
        )}
        {matchExitConfirmOpen && (
          <ExitConfirmOverlay
            onCancel={() => setMatchExitConfirmOpen(false)}
            onConfirm={exitToJoin}
          />
        )}
        {pauseOverlay}
      </main>
    );
  }

  if (view === "loading") {
    return (
      <main className={styles.mobileShell}>
        <section className={styles.splashScreen}>
          <Logo size="large" />
        </section>
      </main>
    );
  }

  if (view === "avatars") {
    return (
      <main className={styles.mobileShell}>
        <TopBar onMenu={toggleMenu} onClose={exitToJoin} />
        {menuOpen && <MenuOverlay onClose={() => setMenuOpen(false)} />}
        <section className={styles.avatarScreen}>
          <h1>Выбери свой аватар</h1>
          <div className={styles.avatarGrid}>
            {avatars.map((avatarItem) => {
              const isTaken = takenAvatarIds.has(avatarItem.id);
              return (
                <button
                  key={avatarItem.id}
                  className={
                    avatarItem.id === currentAvatarId
                      ? styles.avatarOptionSelected
                      : styles.avatarOption
                  }
                  disabled={isTaken}
                  onClick={() => {
                    setSelectedAvatarId(avatarItem.id);
                    store.changeAvatar(avatarItem.id);
                    setView("lobby");
                  }}
                  aria-label={
                    isTaken ? `${avatarItem.name} занят` : avatarItem.name
                  }
                >
                  <img src={avatarItem.src} alt="" />
                </button>
              );
            })}
          </div>
        </section>
        {pauseOverlay}
      </main>
    );
  }

  if (view === "lobby") {
    return (
      <main className={styles.mobileShell}>
        <TopBar onMenu={toggleMenu} onClose={exitToJoin} />
        {menuOpen && <MenuOverlay onClose={() => setMenuOpen(false)} />}
        <section className={styles.lobbyScreen}>
          <h1>Привет {displayName}</h1>
          <button
            className={styles.avatarButton}
            onClick={() => setView("avatars")}
            aria-label="Сменить аватар"
          >
            <img src={selectedAvatar.src} alt="" />
          </button>

          {players.length > 1 && (
            <ul className={styles.lobbyPlayers}>
              {players.map((player) => {
                const playerAvatar = avatarById(player.avatarId);
                return (
                  <li key={player.playerId}>
                    <img src={playerAvatar.src} alt="" />
                    <span>{player.name}</span>
                    <span>{player.ready ? "готов" : "ждет"}</span>
                  </li>
                );
              })}
            </ul>
          )}

          <button
            className={styles.startButton}
            disabled={isLobbyStartLocked}
            onClick={store.startGame}
          >
            {isLobbyStartLocked
              ? "Стартуем"
              : isCurrentPlayerReady
                ? "Не готов"
                : "Готов"}
          </button>
        </section>
        {lobbyStartOverlayLabel && (
          <div className={styles.lobbyCountdownOverlay} aria-live="polite">
            <div className={styles.lobbyCountdownValue}>
              {lobbyStartOverlayLabel}
            </div>
          </div>
        )}
        {pauseOverlay}
      </main>
    );
  }

  return (
    <main className={styles.mobileShell}>
      <TopBar onMenu={toggleMenu} />
      {menuOpen && <MenuOverlay onClose={() => setMenuOpen(false)} />}
      <section className={styles.joinScreen}>
        <label className={styles.joinCard}>
          <span>Код комнаты</span>
          <input
            value={roomInput}
            placeholder="Сюда"
            maxLength={6}
            onChange={(event) => setRoomInput(event.target.value.toUpperCase())}
          />
        </label>

        <label className={styles.joinCard}>
          <span>Имя пользователя</span>
          <input
            value={nameInput}
            placeholder="Сюда"
            onChange={(event) => setNameInput(event.target.value)}
          />
        </label>

        <button
          className={styles.playButton}
          disabled={isJoining}
          onClick={submitJoin}
        >
          {isJoining ? "Подключаемся" : "Играть"}
        </button>

        <p className={styles.terms}>
          Нажимая ИГРАТЬ вы соглашаетесь с{" "}
          <a href="#terms">Условиями пользования.</a>
        </p>

        {store.error && <p className={styles.mobileError}>{store.error}</p>}
      </section>

      {!cookieAccepted && (
        <aside className={styles.cookieBanner}>
          <p>
            Мы используем cookie для улучшения игрового процесса и анализа
            сайта. Узнать больше можно в{" "}
            <a href="#terms">Условиях пользования.</a>
          </p>
          <button
            onClick={() => {
              localStorage.setItem(cookieConsentStorageKey, "true");
              setCookieAccepted(true);
            }}
          >
            Принять
          </button>
        </aside>
      )}
    </main>
  );
}

function MatchMenuOverlay({
  onClose,
  onExit,
}: {
  onClose(): void;
  onExit(): void;
}) {
  return (
    <aside className={styles.matchMenuOverlay} aria-label="Меню матча">
      <div className={styles.matchMenuPanel}>
        <button
          className={styles.matchMenuClose}
          onClick={onClose}
          aria-label="Закрыть меню"
        >
          ×
        </button>
        <div className={styles.matchMenuActions}>
          <button
            className={styles.matchMenuButton}
            onClick={() => {
              window.location.hash = "help";
              onClose();
            }}
          >
            Как играть?
          </button>
          <button className={styles.matchMenuExitButton} onClick={onExit}>
            Выход
          </button>
        </div>
      </div>
    </aside>
  );
}

function ExitConfirmOverlay({
  onCancel,
  onConfirm,
}: {
  onCancel(): void;
  onConfirm(): void;
}) {
  return (
    <aside className={styles.confirmOverlay} aria-label="Подтверждение выхода">
      <div className={styles.confirmPanel}>
        <button
          className={styles.matchMenuClose}
          onClick={onCancel}
          aria-label="Закрыть подтверждение"
        >
          ×
        </button>
        <h2>Выйти из комнаты?</h2>
        <div className={styles.confirmActions}>
          <button className={styles.confirmCancelButton} onClick={onCancel}>
            Остаться
          </button>
          <button className={styles.confirmExitButton} onClick={onConfirm}>
            Выход
          </button>
        </div>
      </div>
    </aside>
  );
}

function PauseOverlay() {
  return (
    <aside className={styles.pauseOverlay} role="status" aria-live="polite">
      <strong>Пауза</strong>
    </aside>
  );
}

function MatchStage({ onExitToJoin }: { onExitToJoin(): void }) {
  const store = useGameStore();
  const [selectedCard, setSelectedCard] = useState("");
  const [dealerStep, setDealerStep] = useState<"cards" | "preview" | "phrase">(
    "cards",
  );
  const [viewerCard, setViewerCard] = useState("");
  const [phrase, setPhrase] = useState("");
  const phase = store.snapshot?.phase ?? Phase.LOBBY;
  const isDealer =
    store.playerId !== "" && store.snapshot?.dealerId === store.playerId;
  const activePlayers = (store.snapshot?.players ?? []).filter(
    (player) => player.playerId !== store.snapshot?.dealerId,
  );
  const hasPlayedCard = store.playedPlayerIds.includes(store.playerId);
  const hasVoted = store.votedPlayerIds.includes(store.playerId);
  const phraseLimit = 150;

  useEffect(() => {
    setSelectedCard("");
    setViewerCard("");
    setDealerStep("cards");
    setPhrase("");
  }, [phase]);

  if (phase === Phase.LOADING) {
    return (
      <section className={styles.matchScreen}>
        <h1>Загрузка</h1>
        <p>Готовим матч. После загрузки устройство отметится на сервере.</p>
      </section>
    );
  }

  if (phase === Phase.TUTORIAL) {
    const playersCount =
      store.tutorialSkipPlayersCount || store.snapshot?.players.length || 0;
    const votesCount = store.tutorialSkipVotesCount;
    const hasSkipped = store.tutorialSkipVotedPlayerIds.includes(
      store.playerId,
    );

    return (
      <section className={styles.matchScreen}>
        <h1>Обучение</h1>
        <p>
          Сейчас показывают обучение. Если все игроки нажмут пропустить, игра
          перейдет дальше.
        </p>
        <button
          className={styles.matchPrimaryButton}
          disabled={hasSkipped}
          onClick={store.skipTutorial}
        >
          Пропустить ({votesCount}/{playersCount})
        </button>
      </section>
    );
  }

  if (phase === Phase.POST_MATCH) {
    return <PostMatchScreen onExitToJoin={onExitToJoin} />;
  }

  if (phase === Phase.SELECT_DEALER && isDealer) {
    return (
      <section className={styles.matchScreen}>
        <div className={styles.dealerIntro}>
          <h1>Твой ход!</h1>
          <p>
            Выбери карту и напиши фразу, так чтобы поняли не все, но хотя бы
            один игрок.
          </p>
        </div>
      </section>
    );
  }

  if (phase === Phase.DEALER_TURN && isDealer) {
    if (dealerStep === "preview") {
      return (
        <section className={styles.dealerPreviewScreen}>
          <ZoomCardView cardId={selectedCard} />
          <div className={styles.dealerPreviewActions}>
            <button
              className={styles.cancelButton}
              onClick={() => setDealerStep("cards")}
              aria-label="Вернуться к выбору карт"
            >
              ×
            </button>
            <button
              className={styles.matchPrimaryButton}
              onClick={() => setDealerStep("phrase")}
            >
              Подтвердить
            </button>
          </div>
        </section>
      );
    }

    if (dealerStep === "phrase") {
      return (
        <section className={styles.dealerPhraseScreen}>
          <h1>Опиши выбранную карту так, чтобы ее отгадали почти все игроки</h1>
          <label className={styles.phraseField}>
            <textarea
              value={phrase}
              maxLength={phraseLimit}
              placeholder="Я сейчас такой скандал учиню!!!!!"
              onChange={(event) => setPhrase(event.target.value)}
            />
            <span>
              {phrase.length}/{phraseLimit}
            </span>
          </label>
          <div className={styles.dealerPreviewActions}>
            <button
              className={styles.cancelButton}
              onClick={() => setDealerStep("preview")}
              aria-label="Вернуться к просмотру карты"
            >
              ×
            </button>
            <button
              className={styles.matchPrimaryButton}
              disabled={!selectedCard || phrase.trim().length === 0}
              onClick={() => store.submitDealerTurn(selectedCard, phrase)}
            >
              Отправить
            </button>
          </div>
        </section>
      );
    }

    return (
      <section className={styles.dealerCardsScreen}>
        <h1>Выбери карту, которой дальше напишешь описание</h1>
        <CardPicker
          cards={store.hand}
          selectedCard={selectedCard}
          onSelect={(cardId) => {
            setSelectedCard(cardId);
            setDealerStep("preview");
          }}
        />
      </section>
    );
  }

  if (phase === Phase.DEALER_TURN && !isDealer) {
    if (viewerCard) {
      return (
        <section className={styles.dealerPreviewScreen}>
          <ZoomCardView cardId={viewerCard} />
          <button
            className={styles.matchPrimaryButton}
            onClick={() => setViewerCard("")}
          >
            Назад
          </button>
        </section>
      );
    }

    return (
      <section className={styles.dealerCardsScreen}>
        <h1>Пока ведущий выбирает карту ты можешь изучить свою руку</h1>
        <CardPicker
          cards={store.hand}
          selectedCard={viewerCard}
          onSelect={setViewerCard}
        />
      </section>
    );
  }

  if (phase === Phase.SHOW_PHRASE) {
    return (
      <section className={styles.matchScreen}>
        <PhraseCallout
          phrase={store.phrase || "Фраза появится на общем экране"}
        />
      </section>
    );
  }

  if (phase === Phase.SHOW_CARDS) {
    return (
      <section className={styles.matchScreen}>
        <h1>Карты на экране</h1>
        <p>Смотри туда.</p>
      </section>
    );
  }

  if (phase === Phase.VOTING) {
    const votingCards =
      !isDealer && store.ownPlayedCardId
        ? store.tableCards.filter((cardId) => cardId !== store.ownPlayedCardId)
        : store.tableCards;

    if (!isDealer && hasVoted) {
      return (
        <TurnStatusScreen
          title="Ждем остальных игроков"
          players={activePlayers}
          completedPlayerIds={store.votedPlayerIds}
        />
      );
    }

    if (viewerCard) {
      return (
        <section className={styles.playerTurnPreviewScreen}>
          <PhraseCallout
            phrase={store.phrase || "Фраза появится на общем экране"}
            compact
          />
          <ZoomCardView cardId={viewerCard} />
          {!isDealer ? (
            <div className={styles.dealerPreviewActions}>
              <button
                className={styles.cancelButton}
                onClick={() => setViewerCard("")}
                aria-label="Вернуться к выбору карт"
              >
                ×
              </button>
              <button
                className={styles.matchPrimaryButton}
                onClick={() => store.vote(viewerCard)}
              >
                Подтвердить
              </button>
            </div>
          ) : (
            <button
              className={styles.matchPrimaryButton}
              onClick={() => setViewerCard("")}
            >
              Назад
            </button>
          )}
        </section>
      );
    }

    return (
      <section className={styles.dealerCardsScreen}>
        <PhraseCallout
          phrase={store.phrase || "Фраза появится на общем экране"}
          compact
        />
        <h1>
          {isDealer
            ? "Игроки голосуют. Можешь изучить карты"
            : "Выбери карту дилера"}
        </h1>
        <CardPicker
          cards={votingCards}
          selectedCard={viewerCard}
          onSelect={setViewerCard}
        />
      </section>
    );
  }

  if (phase === Phase.PLAYERS_TURN && !isDealer) {
    if (hasPlayedCard) {
      return (
        <TurnStatusScreen
          title="Ждем остальных игроков"
          players={activePlayers}
          completedPlayerIds={store.playedPlayerIds}
        />
      );
    }

    if (selectedCard) {
      return (
        <section className={styles.playerTurnPreviewScreen}>
          <PhraseCallout
            phrase={store.phrase || "Фраза появится на общем экране"}
            compact
          />
          <ZoomCardView cardId={selectedCard} />
          <div className={styles.dealerPreviewActions}>
            <button
              className={styles.cancelButton}
              onClick={() => setSelectedCard("")}
              aria-label="Вернуться к выбору карт"
            >
              ×
            </button>
            <button
              className={styles.matchPrimaryButton}
              onClick={() => store.playCard(selectedCard)}
            >
              Подтвердить
            </button>
          </div>
        </section>
      );
    }

    return (
      <section className={styles.dealerCardsScreen}>
        <PhraseCallout
          phrase={store.phrase || "Фраза появится на общем экране"}
          compact
        />
        <h1>Выбери карту</h1>
        <CardPicker
          cards={store.hand}
          selectedCard={selectedCard}
          onSelect={setSelectedCard}
        />
      </section>
    );
  }

  if (phase === Phase.PLAYERS_TURN && isDealer) {
    return (
      <TurnStatusScreen
        title="Игроки выбирают карты"
        players={activePlayers}
        completedPlayerIds={store.playedPlayerIds}
      />
    );
  }

  if (phase === Phase.VOTING_RESULTS) {
    return <VotingResultPersonalScreen />;
  }

  if (phase === Phase.ROUND_RESULT) {
    return <ScoreDeltaScreen />;
  }

  return (
    <section className={styles.matchScreen}>
      <h1>{matchStageTitle(phase)}</h1>
      <p>Что-то показывают на экране, смотри туда.</p>
    </section>
  );
}

function CardPicker({
  cards,
  selectedCard,
  onSelect,
}: {
  cards: string[];
  selectedCard: string;
  onSelect(cardId: string): void;
}) {
  if (cards.length === 0) {
    return <p className={styles.emptyCards}>Карты пока не пришли.</p>;
  }

  return (
    <div className={styles.mobileCardGrid}>
      {cards.map((cardId) => {
        const imageUrl = cardImageUrl(cardId);
        return (
          <button
            key={cardId}
            className={
              selectedCard === cardId
                ? styles.mobileCardSelected
                : styles.mobileCard
            }
            onClick={() => onSelect(cardId)}
          >
            {imageUrl ? (
              <img src={imageUrl} alt={cardId} />
            ) : (
              <span>{cardId}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ZoomCardView({ cardId }: { cardId: string }) {
  const [cardScale, setCardScale] = useState(1);
  const [cardOffset, setCardOffset] = useState({ x: 0, y: 0 });
  const cardFrameRef = useRef<HTMLDivElement | null>(null);
  const cardImageRef = useRef<HTMLImageElement | null>(null);
  const lastPinchDistance = useRef(0);
  const lastTouchPoint = useRef<{ x: number; y: number } | null>(null);
  const lastMousePoint = useRef<{ x: number; y: number } | null>(null);
  const imageUrl = cardImageUrl(cardId);

  const clampOffset = useCallback(
    (offset: { x: number; y: number }, scale: number) => {
      const frame = cardFrameRef.current;
      const image = cardImageRef.current;
      return clampCardOffset(
        offset,
        scale,
        frame?.clientWidth ?? 360,
        frame?.clientHeight ?? 520,
        image?.clientWidth ?? frame?.clientWidth ?? 360,
        image?.clientHeight ?? frame?.clientHeight ?? 520,
      );
    },
    [],
  );

  const updateScale = useCallback(
    (nextScale: number) => {
      const clampedScale = clamp(nextScale, 1, 3);
      setCardScale(clampedScale);
      setCardOffset((offset) => clampOffset(offset, clampedScale));
    },
    [clampOffset],
  );

  const moveCard = useCallback(
    (deltaX: number, deltaY: number, scale = cardScale) => {
      setCardOffset((offset) =>
        clampOffset({ x: offset.x + deltaX, y: offset.y + deltaY }, scale),
      );
    },
    [cardScale, clampOffset],
  );

  useEffect(() => {
    setCardScale(1);
    setCardOffset({ x: 0, y: 0 });
    lastPinchDistance.current = 0;
    lastTouchPoint.current = null;
    lastMousePoint.current = null;
  }, [cardId]);

  useEffect(() => {
    const frame = cardFrameRef.current;
    if (!frame) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      if (event.cancelable) {
        event.preventDefault();
      }
      updateScale(cardScale + (event.deltaY < 0 ? 0.12 : -0.12));
    };

    frame.addEventListener("wheel", onWheel, { passive: false });
    return () => frame.removeEventListener("wheel", onWheel);
  }, [cardScale, updateScale]);

  return (
    <div
      ref={cardFrameRef}
      className={styles.zoomCard}
      onMouseDown={(event) => {
        if (cardScale <= 1) {
          return;
        }

        event.preventDefault();
        lastMousePoint.current = { x: event.clientX, y: event.clientY };
      }}
      onMouseMove={(event) => {
        if (!lastMousePoint.current) {
          return;
        }

        event.preventDefault();
        moveCard(
          event.clientX - lastMousePoint.current.x,
          event.clientY - lastMousePoint.current.y,
        );
        lastMousePoint.current = { x: event.clientX, y: event.clientY };
      }}
      onMouseUp={() => {
        lastMousePoint.current = null;
      }}
      onMouseLeave={() => {
        lastMousePoint.current = null;
      }}
      onTouchStart={(event) => {
        preventTouchGesture(event);

        if (event.touches.length === 2) {
          lastPinchDistance.current = touchDistance(
            event.touches[0],
            event.touches[1],
          );
          lastTouchPoint.current = touchCenter(
            event.touches[0],
            event.touches[1],
          );
          return;
        }

        if (event.touches.length === 1 && cardScale > 1) {
          lastTouchPoint.current = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
          };
        }
      }}
      onTouchMove={(event) => {
        preventTouchGesture(event);

        if (event.touches.length === 2 && lastPinchDistance.current !== 0) {
          const nextDistance = touchDistance(
            event.touches[0],
            event.touches[1],
          );
          const nextCenter = touchCenter(event.touches[0], event.touches[1]);
          const nextScale = clamp(
            cardScale + (nextDistance - lastPinchDistance.current) / 180,
            1,
            3,
          );
          const lastCenter = lastTouchPoint.current ?? nextCenter;

          lastPinchDistance.current = nextDistance;
          lastTouchPoint.current = nextCenter;
          setCardScale(nextScale);
          setCardOffset((offset) =>
            clampOffset(
              {
                x: offset.x + nextCenter.x - lastCenter.x,
                y: offset.y + nextCenter.y - lastCenter.y,
              },
              nextScale,
            ),
          );
          return;
        }

        if (
          event.touches.length === 1 &&
          cardScale > 1 &&
          lastTouchPoint.current
        ) {
          const nextPoint = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
          };
          moveCard(
            nextPoint.x - lastTouchPoint.current.x,
            nextPoint.y - lastTouchPoint.current.y,
          );
          lastTouchPoint.current = nextPoint;
        }
      }}
      onTouchEnd={() => {
        lastPinchDistance.current = 0;
        lastTouchPoint.current = null;
      }}
      onTouchCancel={() => {
        lastPinchDistance.current = 0;
        lastTouchPoint.current = null;
      }}
    >
      {imageUrl ? (
        <img
          ref={cardImageRef}
          src={imageUrl}
          alt={cardId}
          onLoad={() => {
            setCardOffset((offset) => clampOffset(offset, cardScale));
          }}
          style={{
            transform: `translate3d(${cardOffset.x}px, ${cardOffset.y}px, 0) scale(${cardScale})`,
          }}
        />
      ) : (
        <span>{cardId}</span>
      )}
    </div>
  );
}

function PhraseCallout({
  phrase,
  compact = false,
}: {
  phrase: string;
  compact?: boolean;
}) {
  return (
    <div
      className={compact ? styles.phraseCalloutCompact : styles.phraseCallout}
    >
      <span>Фраза</span>
      <strong>{phrase}</strong>
    </div>
  );
}

function PostMatchScreen({ onExitToJoin }: { onExitToJoin(): void }) {
  const store = useGameStore();
  const playersCount =
    store.postMatchPlayersCount || store.snapshot?.players.length || 0;
  const continuedCount = store.postMatchContinuedCount;
  const hasContinued = store.postMatchContinuedPlayerIds.includes(
    store.playerId,
  );

  return (
    <section className={styles.postMatchScreen}>
      <h1>Сыграть еще раз?</h1>
      <p>
        Готовы продолжить: {continuedCount}/{playersCount}
      </p>
      <div className={styles.postMatchActions}>
        <button
          className={styles.matchPrimaryButton}
          disabled={hasContinued}
          onClick={store.continueAfterMatch}
        >
          Продолжить
        </button>
        <button className={styles.postMatchExitButton} onClick={onExitToJoin}>
          Выход
        </button>
      </div>
    </section>
  );
}

function ScoreDeltaScreen() {
  const store = useGameStore();
  const players = store.snapshot?.players ?? [];
  const currentPlayer = players.find(
    (player) => player.playerId === store.playerId,
  );
  const otherPlayers = players.filter(
    (player) => player.playerId !== store.playerId,
  );
  const orderedPlayers = currentPlayer
    ? [currentPlayer, ...otherPlayers]
    : players;
  const hasDelta = Object.keys(store.playerResultDelta).length > 0;

  return (
    <section className={styles.scoreScreen}>
      <h1>{hasDelta ? "Итоги голосования" : "Считаем очки"}</h1>
      <div className={styles.scoreList}>
        {orderedPlayers.map((player, index) => {
          const avatarItem = avatarById(player.avatarId);
          const delta = store.playerResultDelta[player.playerId] ?? 0;
          const total = store.playerResults[player.playerId];
          const isNegative = delta < 0;
          const deltaLabel = delta > 0 ? `+${delta}` : String(delta);
          return (
            <Fragment key={player.playerId}>
              {index === 1 && <div className={styles.scoreDivider} />}
              <article className={styles.scoreRow}>
                <div className={styles.scoreAvatarWrap}>
                  <img src={avatarItem.src} alt="" />
                  <span
                    className={
                      isNegative
                        ? styles.scoreDeltaNegative
                        : styles.scoreDeltaBubble
                    }
                  >
                    {hasDelta ? deltaLabel : "..."}
                  </span>
                </div>
                <div className={styles.scorePlayerMeta}>
                  <strong>{player.name}</strong>
                  <span>
                    {total === undefined
                      ? "Очки скоро появятся"
                      : `${total} очков`}
                  </span>
                </div>
              </article>
            </Fragment>
          );
        })}
      </div>
    </section>
  );
}

function VotingResultPersonalScreen() {
  const store = useGameStore();
  const players = store.snapshot?.players ?? [];
  const isDealer =
    store.playerId !== "" && store.snapshot?.dealerId === store.playerId;
  const ownCard = isDealer
    ? store.dealerCard
    : store.playerPlays[store.playerId] || store.ownPlayedCardId;
  const votedCard = store.playerVotes[store.playerId] || "";
  const votedDealer = votedCard !== "" && votedCard === store.dealerCard;
  const votedOwnerId = votedDealer
    ? (store.snapshot?.dealerId ?? "")
    : findPlayerByCard(store.playerPlays, votedCard);
  const votedOwner = players.find((player) => player.playerId === votedOwnerId);
  const votedAvatar = avatarById(votedOwner?.avatarId ?? "");
  const votersForYou = ownCard
    ? Object.entries(store.playerVotes)
        .filter(([, cardId]) => cardId === ownCard)
        .filter(([playerId]) => playerId !== store.playerId)
        .map(([playerId]) =>
          players.find((player) => player.playerId === playerId),
        )
        .filter((player): player is NonNullable<typeof player> =>
          Boolean(player),
        )
    : [];

  return (
    <section className={styles.votingResultScreen}>
      <h1>Итоги голосования</h1>

      {!isDealer && (
        <article
          className={votedDealer ? styles.votedDealerCard : styles.votedCard}
        >
          <span>
            {votedDealer ? "Ты проголосовал за ведущего" : "Ты проголосовал за"}
          </span>
          {votedOwner ? (
            <>
              <img src={votedAvatar.src} alt="" />
              <strong>{votedOwner.name}</strong>
            </>
          ) : (
            <strong>карту без владельца</strong>
          )}
        </article>
      )}

      <section className={styles.votersPanel}>
        <h2>За тебя проголосовали</h2>
        {votersForYou.length === 0 ? (
          <div className={styles.emptyVoters}>Пока никто</div>
        ) : (
          <div className={styles.votersList}>
            {votersForYou.map((player) => {
              const avatarItem = avatarById(player.avatarId);
              return (
                <div key={player.playerId} className={styles.voterChip}>
                  <img src={avatarItem.src} alt="" />
                  <span>{player.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </section>
  );
}

function TurnStatusScreen({
  title,
  players,
  completedPlayerIds,
}: {
  title: string;
  players: Array<{ playerId: string; name: string; avatarId: string }>;
  completedPlayerIds: string[];
}) {
  const store = useGameStore();
  const phase = store.snapshot?.phase ?? Phase.LOBBY;
  const shouldShowPhrase = phase === Phase.VOTING && store.phrase;

  return (
    <section className={styles.turnStatusScreen}>
      {shouldShowPhrase && <PhraseCallout phrase={store.phrase} compact />}
      <h1>{title}</h1>
      <div className={styles.turnStatusList}>
        {players.map((player) => {
          const avatarItem = avatarById(player.avatarId);
          const isDone = completedPlayerIds.includes(player.playerId);

          return (
            <article key={player.playerId} className={styles.turnStatusRow}>
              <img src={avatarItem.src} alt="" />
              <strong>{player.name}</strong>
              <span
                className={
                  isDone ? styles.turnDoneMark : styles.turnPendingMark
                }
              >
                {isDone ? "✓" : ""}
              </span>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function findPlayerByCard(playerPlays: Record<string, string>, cardId: string) {
  return (
    Object.entries(playerPlays).find(
      ([, playedCardId]) => playedCardId === cardId,
    )?.[0] ?? ""
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function clampCardOffset(
  offset: { x: number; y: number },
  scale: number,
  width: number,
  height: number,
  contentWidth = width,
  contentHeight = height,
) {
  if (scale <= 1) {
    return { x: 0, y: 0 };
  }

  const maxOffsetX = Math.max(0, (contentWidth * scale - width) / 2);
  const maxOffsetY = Math.max(0, (contentHeight * scale - height) / 2);
  return {
    x: clamp(offset.x, -maxOffsetX, maxOffsetX),
    y: clamp(offset.y, -maxOffsetY, maxOffsetY),
  };
}

function touchDistance(
  first: { clientX: number; clientY: number },
  second: { clientX: number; clientY: number },
) {
  return Math.hypot(
    first.clientX - second.clientX,
    first.clientY - second.clientY,
  );
}

function touchCenter(
  first: { clientX: number; clientY: number },
  second: { clientX: number; clientY: number },
) {
  return {
    x: (first.clientX + second.clientX) / 2,
    y: (first.clientY + second.clientY) / 2,
  };
}

function preventTouchGesture(event: TouchEvent<HTMLElement>) {
  if (event.cancelable) {
    event.preventDefault();
  }

  event.stopPropagation();
}

function avatar(id: string, name: string) {
  const [, assetId] = id.split(":");
  return {
    id,
    name,
    src: `/art/avatars/base/${assetId}.png`,
  };
}

function avatarById(avatarId: string) {
  return avatars.find((item) => item.id === avatarId) ?? avatars[0];
}

function matchStageTitle(phase: Phase) {
  switch (phase) {
    case Phase.SELECT_DEALER:
      return "Выбор дилера";
    case Phase.SHOW_PHRASE:
      return "Фраза";
    case Phase.SHOW_CARDS:
      return "Карты на столе";
    case Phase.VOTING_RESULTS:
      return "Итоги голосования";
    case Phase.ROUND_RESULT:
      return "Итоги раунда";
    case Phase.MATCH_RESULT:
      return "Итоги матча";
    case Phase.POST_MATCH:
      return "Матч завершен";
    default:
      return "Матч";
  }
}

function TopBar({
  onMenu,
  onClose,
}: {
  onMenu?: () => void;
  onClose?: () => void;
}) {
  return (
    <header className={styles.topBar}>
      {onMenu ? (
        <button
          className={styles.menuButton}
          onClick={onMenu}
          aria-label="Меню"
        >
          <span />
          <span />
          <span />
        </button>
      ) : (
        <span />
      )}
      <Logo size="small" />
      {onClose ? (
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Exit to main menu"
        >
          ×
        </button>
      ) : (
        <PhaseTimer />
      )}
    </header>
  );
}

function PhaseTimer() {
  const snapshot = useGameStore((state) => state.snapshot);
  const isPaused = useGameStore((state) => state.isPaused);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const deadlineMs = snapshot?.hasDeadline
    ? Number(snapshot.deadlineUnixMs)
    : 0;
  const remainingMs = Math.max(0, deadlineMs - nowMs);
  const remainingSec = Math.ceil(remainingMs / 1000);

  useEffect(() => {
    if (!deadlineMs) {
      return;
    }

    if (isPaused) {
      setNowMs(Date.now());
      return;
    }

    setNowMs(Date.now());
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [deadlineMs, isPaused]);

  if (!deadlineMs || remainingSec <= 0) {
    return <span />;
  }

  const minutes = Math.floor(remainingSec / 60);
  const seconds = remainingSec % 60;
  const label = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  return (
    <span
      className={
        remainingSec <= 10 ? styles.phaseTimerDanger : styles.phaseTimer
      }
      aria-label="Phase timer"
    >
      {label}
    </span>
  );
}

function MenuOverlay({ onClose }: { onClose(): void }) {
  return (
    <aside className={styles.menuOverlay} aria-label="Меню">
      <div className={styles.menuList}>
        <a className={styles.menuItem} href="#help" onClick={onClose}>
          Помощь
        </a>
        <a className={styles.menuItem} href={publicGameUrl} onClick={onClose}>
          Наш сайт
        </a>
        <a className={styles.menuItem} href="#games" onClick={onClose}>
          Еще игры
        </a>
        <a className={styles.menuItem} href="#settings" onClick={onClose}>
          Настройки
        </a>
      </div>

      <nav className={styles.socialLinks} aria-label="Социальные сети">
        <a href="https://youtube.com" aria-label="YouTube">
          ▻
        </a>
        <a href="https://instagram.com" aria-label="Instagram">
          ◎
        </a>
        <a href="https://telegram.org" aria-label="Telegram">
          ◁
        </a>
      </nav>
    </aside>
  );
}

function Logo({ size }: { size: "small" | "large" }) {
  return (
    <div
      className={size === "large" ? styles.logoLarge : styles.logoSmall}
      aria-label="Овощной переполох"
    >
      <span>Овощной</span>
      <strong>переполох</strong>
    </div>
  );
}
